import * as shared from './shared';
import { BrandLogoColorBackgroundSpecific, ITheme } from '.';
import { css } from 'styled-components';

const colors: ITheme['colors'] = {
  white: '#ffffff',
  grey100: '#f5f5f5',
  grey200: '#eaeaea',
  grey300: '#d8d8d8',
  grey400: '#cccccc',
  grey450: '#979797',
  grey500: '#696969',
  grey600: '#cbdbf1',
  positive: '#E6F5F1',
  negative: '#FFEBEC',
  neutral: '#8D8D8D',
  black: '#202124',
  green: '#21933B',
  red: '#EF0107',
  darkRed: '#9D2D2A',
  brown100: '#98845A',
  negativeRed: '#B60000',
  brand: {
    primary: '#01172E',
    secondary: '#FDB22B',
    tertiary: '#37475A',
    quaternary: '#162750',
    foreground: '#01172E',
    primary75: 'rgba(1, 23, 46, 0.75)',
    primary50: 'rgba(1, 23, 46, 0.50)',
    primary25: 'rgba(1, 23, 46, 0.25)',
    secondary75: 'rgba(253, 178, 43, 0.75)',
    secondary50: 'rgba(253, 178, 43, 0.5)',
    secondary25: 'rgba(253, 178, 43, 0.25)',
    tertiary75: 'rgba(55, 71, 90, 0.75)',
    tertiary50: 'rgba(55, 71, 90, 0.50)',
    tertiary25: 'rgba(55, 71, 90, 0.25)',
    highlight: '#FDB22B',
    sgp: '#128152'
  },
  modals: {
    success: '#1BC47D',
    warning: '#FCB514',
    error: '#EA1319',
    expired: '#000000',
    neutral: '#DADADA'
  },
  buttons: {
    primary: '#4DC9AA',
    primaryContrast: '#01172E',
    secondary: '#ebf1ff',
    alert: '#EF0107'
  },
  supporting: {
    primary: '#FDB22B',
    secondary: '#FFCA06',
    tertiary: '#00AFFF',
    tertiarylight: '#4DC7FF',
    accent100: '#ECF0F4',
    accent200: '#DCECF9',
    accent300: '#DAE1EA',
    accent400: '#9AA1AF',
    accent500: '#7383a6',
    accent600: '#D9E5F6',
    accent700: '#F7FAFF',
    focusShadow: '#0075D4'
  },
  status: {
    error: '#EF0107',
    error50: '#F6CCD166',
    warning: '#FDB22B',
    warning15: '#F5A62319',
    success: '#4DC9AA',
    info: '#E5E9EE',
    expired: '#000000',
    unreachable: '#7a7f82',
    alert: '#fff6e3'
  },
  accountTypes: {
    all: '#25306A',
    writer: '#44d7b6',
    dok: '#0075D4',
    kiosk: '#7f34fc',
    safe: '#fb4281',
    cage: '#fa6400'
  },
  liveScore: '#3600D0',
  liveScoreAccent: '#FBF4FF',
  oddsBoost: '#00C785',
  cop: {
    selection: '#FFF8E1'
  },
  toggle: {
    enabledBackgroundColour: '#1BC47D',
    disabledBackgroundColour: '#DADADA',
    textColour: '#343840'
  },
  videowall: '#11192D',
  price: '#128152',
  unavailable: '#DADADA'
};

const WILLIAM_HILL: ITheme = {
  baseFontSize: '62.5%',
  colors,
  components: {
    ...shared.components,
    global: {
      textColor: colors.brand.primary,
      overlay: colors.brand.primary50,
      link: colors.supporting.tertiary
    },
    buttons: {
      secondary: {
        borderColor: colors.brand.tertiary
      },
      tertiary: {
        background: colors.brand.secondary,
        border: colors.brand.secondary,
        color: colors.brand.primary
      }
    },
    header: {
      ...shared.components.header,
      background: colors.brand.primary
    },
    login: {
      background: colors.brand.primary
    },
    homePage: {
      background: colors.brand.quaternary
    },
    markets: {
      color: colors.brand.primary
    },
    navigation: {
      clock: '#fcb514',
      header: colors.brand.primary,
      menu: {
        background: colors.brand.primary,
        active: colors.brand.tertiary,
        activeBorder: colors.brand.secondary
      },
      tabs: {
        container: colors.brand.tertiary,
        item: colors.supporting.accent200
      },
      counter: {
        color: colors.brand.primary,
        background: colors.brand.secondary,
        active: colors.brand.primary
      },
      segmentedMenu: {
        selected: {
          color: colors.brand.primary,
          background: colors.brand.secondary
        }
      }
    },
    betslip: {
      tabs: {
        active: colors.supporting.secondary
      },
      keypad: {
        background: colors.brand.primary25
      },
      price: {
        fontColor: colors.brand.primary
      },
      teaser: {
        border: 'transparent',
        background: colors.supporting.accent200,
        selected: {
          color: '#FCB514',
          background: colors.brand.primary
        }
      },
      footer: colors.brand.primary
    },
    betWithCredits: {
      border: '#128152',
      text: '#128152',
      background: '#F4FCF2',
      stakeInput: '#0075D4',
      instructionalText: '#5A5E61'
    },
    cashOut: {
      betDetails: {
        bottomStripColor: '#000000'
      },
      buttons: {
        primary: {
          backgroundColor: colors.brand.secondary,
          disabled: {
            backgroundColor: colors.brand.primary
          }
        },
        secondary: {
          backgroundColor: '#1BC47D',
          color: colors.black
        }
      }
    },
    stake: {
      fontColor: '#0075D4',
      borderColor: '#0075D4'
    },
    trendingBetPrompt: {
      borderColour: colors.brand.sgp,
      backgroundColour: '#F4FCF2',
      selectionBackgroundColour: '#F4FCF2',
      ribbon: {
        textColour: colors.white,
        backgroundColour: colors.brand.sgp
      },
      leg: {
        selected: colors.brand.sgp,
        unselected: '#dadada',
        unselectedTextColor: colors.brand.primary
      },
      price: {
        backgroundColor: colors.supporting.accent200,
        borderColor: colors.supporting.accent200,
        priceTextColor: colors.brand.primary,
        textColor: colors.neutral,
        payoutTextColor: colors.brand.primary
      },
      button: {
        textColor: '#343840',
        borderColor: colors.brand.primary
      }
    },
    sports: {
      accordion: {
        header: colors.brand.primary
      },
      selections: {
        color: colors.supporting.accent200,
        borderColor: colors.supporting.accent200,
        selected: {
          background: colors.brand.primary,
          color: colors.supporting.secondary
        }
      }
    },
    rewards: {
      inactive: colors.brand.secondary,
      background: '#000000',
      detailsMenu: {
        count: css`
          background-color: #fcb514;
          color: ${colors.brand.primary};
        `,
        inactive: css`
          border-bottom: 1px solid #5a5e61;
          color: #5a5e61;
        `,
        active: css`
          border-bottom: 4px solid #fcb514;
          color: #343840;
          font-weight: 600;
        `
      }
    },

    posSecondary: {
      headerBackgroundColor: '#E5E5E5',
      priceColor: '#128152',
      shadowColor: colors.black,
      totalsBackgroundColor: '#2E3034'
    },
    cannedParlays: {
      kioskSecondaryBackground: css`
        background-color: ${colors.brand.quaternary};
      `,
      kioskSecondaryLegDotsColour: colors.grey300,
      priceButton: css`
        border: none;
      `,
      priceText: css`
        color: #01172e;
      `
    },
    buyingPoints: {
      toggleBackground: colors.brand.primary,
      toggleArrow: colors.toggle.enabledBackgroundColour,
      stroke: colors.brand.foreground
    },
    mobile: {
      heading: {
        fontFamily: 'oswald, sans-serif'
      },
      tab: {
        selection: '#255E46',
        item: '#5A5E61'
      },
      scan: {
        background: '#2fb57d'
      }
    },
    images: {
      stroke: colors.brand.primary
    },
    parlayCards: {
      autoPick: {
        item: {
          selected: css`
            background-color: #01172e;
            color: #fcb514;
          `
        },
        section: css`
          background-color: #37475a;
        `
      }
    },
    notification: {
      background: colors.darkRed,
      color: colors.white,
      fontWeight: 'bold'
    },
    parlayBuilder: {
      header: css`
        background-color: ${colors.brand.primary};
      `,
      item: {
        selected: css`
          border-left: 0.5rem solid ${colors.brand.secondary};
        `
      },
      subitem: {
        selected: css`
          ${shared.components.parlayBuilder.subitem.selected}
          background-color: ${colors.brand.secondary};
          color: ${colors.brand.primary};
        `
      }
    },
    bannerGradients: {
      0: '#696969',
      1: '#01172E',
      2: '#000000',
      3: '#243B68',
      4: '#37475A'
    },
    comps: {
      iconColor: '#01172E'
    },
    card: {
      header: css`
        background-color: ${colors.brand.primary};
        color: ${colors.white};
      `
    },
    checkbox: {
      backgroundColour: '#F9F9F9',
      disabledColour: '#8D8D8D',
      checkedColour: '#0075D4'
    },
    enhancedSearch: {
      input: {
        background: '#0b162ae6',
        textColor: colors.supporting.accent300
      },
      getStarted: {
        magnifyingGlass: colors.brand.tertiary,
        clearPins: '#0075d4'
      }
    },
    keyboard: {
      background: 'linear-gradient(90deg, #0b162ae6, #071025e6)',
      keys: '#555a58',
      buttons: '#e9ebea'
    }
  },
  secondaryBannerFonts: {
    small: '4.2rem',
    regular: '5.8rem',
    large: '7.4rem',
    xlarge: '10rem',
    topXl: '12.8rem'
  },
  borderRadius: {
    small: '2px',
    medium: '4px',
    large: '8px',
    xlarge: '10px',
    xxlarge: '20px',
    xxxlarge: '25px'
  },
  generic: {
    buttonHeight: '4.6rem',
    contextMenuHeight: '16.3rem',
    headerHeight: '9.4rem'
  },
  layers: {
    main: 100,
    betslip: 200,
    keypads: 300,
    topmenus: 400,
    tooltips: 500,
    modals: 600,
    timeoutWarning: 650,
    lock: 700,
    networkLoss: 800
  },
  shadows: {
    drop: '0 0 15px 0 rgba(1, 23, 46, 0.15)',
    veryheavy: '0 0 20px 0 rgba(0, 0, 0, 0.3)',
    heavy: '0 0 10px 0 rgba(9, 69, 189, 0.2)',
    light: '0 0 5px 0 rgba(0, 175, 255, 0.8)',
    lightred: '0 0 5px 0 rgba(208, 2, 27, 0.8)',
    lightFocus: '0 0 5px 0 rgba(0,117,212,1)'
  }
};

export default WILLIAM_HILL;

export const brandLogoColors: {
  hill: BrandLogoColorBackgroundSpecific;
  strapline: BrandLogoColorBackgroundSpecific;
  william: string;
} = {
  hill: {
    onBackground: {
      dark: '#ffffff',
      light: '#171a45'
    }
  },
  strapline: {
    onBackground: {
      dark: '#ffffff',
      light: '#000000'
    }
  },
  william: '#f8b618'
} as const;
